/**
 * API Response Types for Personal Trainer App
 *
 * This file contains TypeScript interfaces and types for API responses,
 * request payloads, and common API patterns used throughout the application.
 *
 * Features:
 * - Standardized API response wrappers
 * - Pagination response types (re-exported from common/pagination.types)
 * - Error response types (re-exported from api/errors)
 * - Request/response type pairs for all endpoints
 * - Generic API utility types
 */

import type { ApiErrorResponse } from './api/errors';
import type { PendingSignUp, User } from './auth';

// Re-export pagination types from centralized location (single source of truth)
export type {
  PaginatedResponse,
  PaginationParams,
  SearchParams,
  DateRangeParams,
  PaginatedResponseData,
} from './common/pagination.types';
export { isPaginatedResponse } from './common/pagination.types';

// Re-export ApiErrorResponse from centralized location
export type { ApiErrorResponse };

// =============================================================================
// BASE API RESPONSE TYPES
// =============================================================================

/**
 * Generic API response wrapper for Laravel success() macro
 * Laravel success() macro wraps data in 'data' key
 *
 * `metadata` is typed separately from `data` because the macro sends it
 * alongside rather than inside: `response()->success(data:, metadata:)` emits
 * `{ data, metadata }`, and the second key is omitted entirely when empty.
 * Endpoints that report something about the LIST rather than about a row —
 * which tier answered, what cap was applied — put it there, so a caller that
 * reads only `.data.data` silently drops it.
 */
export interface SuccessApiResponse<T = unknown, M = Record<string, unknown>> {
  data: T;
  message?: string;
  metadata?: M;
}

/**
 * Generic API response wrapper for Laravel created() macro
 * Laravel created() macro returns status and message
 */
export interface CreatedApiResponse {
  status: 'success';
  message: string;
  [key: string]: unknown; // Additional optional data
}

/**
 * Generic API response wrapper for Laravel warning() macro
 * Laravel warning() macro returns status and message
 */
export interface WarningApiResponse {
  status: 'warning';
  message: string;
}

/**
 * Generic API response wrapper for Laravel accepted() macro
 * Laravel accepted() macro returns status and message
 */
export interface AcceptedApiResponse {
  status: string; // Usually 'queued' or custom status
  message: string;
  [key: string]: unknown; // Additional optional data
}

/**
 * Generic API response wrapper for Laravel invalid() macro
 * Laravel invalid() macro returns status, message, and errors
 */
export interface InvalidApiResponse {
  status: 'error';
  message: string;
  errors?: Record<string, string[]>;
}

/**
 * Generic API response wrapper for Laravel payment() macro
 * Laravel payment() macro returns subscription info
 */
export interface PaymentRequiredApiResponse {
  subscription: {
    required: string;
    current: string;
  };
  message: string;
}

/**
 * Generic API response wrapper for Laravel forbidden() macro
 * Laravel forbidden() macro returns status and message
 */
export interface ForbiddenApiResponse {
  status: 'access_denied';
  message: string;
}

/**
 * Generic API response wrapper for Laravel error() macro
 * Laravel error() macro returns status, message, and optional error details
 */
export interface ErrorApiResponse {
  status: 'error';
  message: string;
  error?: string;
}

// PaginatedResponse is re-exported from common/pagination.types above
// ApiErrorResponse is re-exported from api/errors above

// =============================================================================
// AUTHENTICATION API TYPES
// =============================================================================

// ApiUser is now User from auth.ts - no duplication needed

/**
 * Token data structure for API responses
 */
export interface ApiToken {
  access_token: string;
  refresh_token?: string;
  token_type: 'Bearer';
  expires_in: number;
  expires_at?: string;
  scope?: string[];
}

// =============================================================================
// TOKEN-BASED AUTHENTICATION (Mobile)
// =============================================================================

/**
 * Token-based login API response (for mobile)
 * Uses generic SuccessApiResponse with login data
 */
export type TokenLoginApiResponse = SuccessApiResponse<{
  user: User;
  token: ApiToken;
}>;

/**
 * `POST /sanctum/token` for an account with two-factor on: the password was
 * right, and no bearer comes until the challenge is answered with a code at
 * `POST /sanctum/token/two-factor`, which answers as `TokenLoginApiResponse`.
 * The challenge is opaque, lasts until `expires_at` (ten minutes), survives a
 * wrong code and is spent by the first right one.
 */
export interface TokenTwoFactorChallenge {
  two_factor: true;
  challenge: string;
  expires_at: string;
}

/** `POST /sanctum/token`: signed in, or a two-factor challenge to answer. */
export type TokenPasswordLoginApiResponse = SuccessApiResponse<
  TokenLoginApiResponse['data'] | TokenTwoFactorChallenge
>;

/**
 * An app's social sign-in that found no account (202): the pending sign-up the
 * user answers at `/api/auth/social/sign-up`.
 */
export type PendingSignUpApiResponse = SuccessApiResponse<{
  pending_sign_up: PendingSignUp;
}>;

// =============================================================================
// SEARCH API TYPES
// =============================================================================

/**
 * Search API response
 */
export interface SearchApiResponse<T> {
  data: T[];
  query: string;
  filters: Record<string, unknown>;
  total: number;
  page: number;
  per_page: number;
  suggestions?: string[];
}

// =============================================================================
// REQUEST PAYLOAD TYPES
// =============================================================================

/**
 * Generic API request payload
 */
export interface ApiRequestPayload<T = unknown> {
  data?: T;
  meta?: {
    timestamp: string;
    request_id?: string;
    version?: string;
  };
}

// PaginationParams, SearchParams, and DateRangeParams are re-exported from
// common/pagination.types above (single source of truth).

// =============================================================================
// API ENDPOINT TYPES
// =============================================================================

/**
 * API endpoint configuration
 */
export interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  requiresAuth: boolean;
  rateLimit?: {
    requests: number;
    window: number; // in seconds
  };
}

/**
 * API endpoint definitions.
 *
 * Not exhaustive: most service classes hardcode their own endpoints, so look
 * for those in the service. Only add an entry here for a service that reads
 * its paths from this table.
 */
export const API_ENDPOINTS = {
  // Authentication - Token-based (Mobile)
  TOKEN_LOGIN: {
    method: 'POST',
    path: '/sanctum/token',
    requiresAuth: false,
  },
  TOKEN_REGISTER: {
    method: 'POST',
    path: '/api/register', // with `device_name`, it answers a bearer
    requiresAuth: false,
  },
  // Asking for a password-reset link from an app: the stateless twin of
  // Fortify's `/forgot-password` (PASSWORD_RESET_REQUEST), which sits behind
  // the CSRF check no bearer client can pass. Same body, same answers.
  TOKEN_PASSWORD_RESET_REQUEST: {
    method: 'POST',
    path: '/api/forgot-password',
    requiresAuth: false,
  },
  // Revokes the bearer the request is signed with — this device only.
  TOKEN_LOGOUT: {
    method: 'DELETE',
    path: '/sanctum/token',
    requiresAuth: true,
  },
  // Signed with the still-valid bearer; `{token}` names which one to swap.
  REFRESH_TOKEN: {
    method: 'PUT',
    path: '/sanctum/token/{token}',
    requiresAuth: true,
  },

  // Authentication - Session-based (Web)
  SESSION_LOGIN: {
    method: 'POST',
    path: '/login',
    requiresAuth: false,
  },
  SESSION_REGISTER: {
    method: 'POST',
    path: '/api/register',
    requiresAuth: false,
  },
  SESSION_LOGOUT: {
    method: 'POST',
    path: '/logout',
    requiresAuth: true,
  },
  // An admin signed in as a member from the admin panel goes back to being
  // the admin: 204, or 403 when the session isn't impersonating anyone.
  IMPERSONATE_LEAVE: {
    method: 'POST',
    path: '/api/impersonate/leave',
    requiresAuth: true,
  },
  // Finishing a two-factor sign-in with a code or a recovery code: the
  // website's pending sign-in waits in its session, and the apps answer the
  // challenge `/sanctum/token` gave them. Both at the API root.
  TWO_FACTOR_CHALLENGE: {
    method: 'POST',
    path: '/two-factor-challenge',
    requiresAuth: false,
  },
  TOKEN_TWO_FACTOR_CHALLENGE: {
    method: 'POST',
    path: '/sanctum/token/two-factor',
    requiresAuth: false,
  },
  CSRF_INIT: {
    method: 'GET',
    path: '/sanctum/csrf-cookie',
    requiresAuth: false,
  },

  // Authentication - Social (Apps). The apps post the provider's tokens; an
  // account answers with the TOKEN_LOGIN payload, and none yet answers 202
  // with a pending sign-up.
  GOOGLE_SIGN_IN: {
    method: 'POST',
    path: '/api/auth/social/google',
    requiresAuth: false,
  },
  APPLE_SIGN_IN: {
    method: 'POST',
    path: '/api/auth/social/apple',
    requiresAuth: false,
  },
  // A pending sign-up, answered once. The website reads it and answers with its
  // session; an app answers with the pending sign-up's token.
  GET_PENDING_SIGN_UP: {
    method: 'GET',
    path: '/api/auth/social/sign-up',
    requiresAuth: false,
  },
  COMPLETE_SIGN_UP: {
    method: 'POST',
    path: '/api/auth/social/sign-up',
    requiresAuth: false,
  },
  DECLINE_SIGN_UP: {
    method: 'DELETE',
    path: '/api/auth/social/sign-up',
    requiresAuth: false,
  },

  // Authentication - Social (Web). Top-level navigations, never fetch or XHR,
  // to these API-root paths: build the URL on getAuthBaseUrl(). `{provider}`
  // is `google` or `apple`. Sign-in ends on the SPA's /login; confirm and
  // link end on /settings with `account_flow` and `result`.
  SOCIAL_REDIRECT: {
    method: 'GET',
    path: '/auth/{provider}/redirect',
    requiresAuth: false,
  },
  SOCIAL_CONFIRM: {
    method: 'GET',
    path: '/auth/{provider}/confirm',
    requiresAuth: true,
  },
  SOCIAL_LINK: {
    method: 'GET',
    path: '/auth/{provider}/link',
    requiresAuth: true,
  },

  // Authentication - Common
  // The website's reset-link request (Fortify, behind the CSRF check); the
  // apps use TOKEN_PASSWORD_RESET_REQUEST.
  PASSWORD_RESET_REQUEST: {
    method: 'POST',
    path: '/forgot-password',
    requiresAuth: false,
  },
  PASSWORD_RESET_CONFIRM: {
    method: 'POST',
    path: '/reset-password',
    requiresAuth: false,
  },
  // The emailed verification link's call, made by the website's public
  // /verify-email page with the session and by the apps with the bearer. Its
  // signature covers the whole URL, and the link is signed for this route, so
  // it's sent here at the API's host (Fortify's root route refuses it), with
  // only the link's `expires` and `signature` in the query.
  VERIFY_EMAIL: {
    method: 'GET',
    path: '/api/email/verify/{id}/{hash}',
    requiresAuth: true,
  },
  // A new verification link. The website's route sits at the API root (session
  // and CSRF), where a bearer is refused; the apps' is the same path under
  // `/api`, which is `apiClient`'s base.
  RESEND_VERIFICATION: {
    method: 'POST',
    path: '/email/verification-notification',
    requiresAuth: true,
  },
  AUTH_USER: {
    method: 'GET',
    path: '/user/auth',
    requiresAuth: true,
  },
  // AUTH_USER addressed from the API root, for the auth client: the website's
  // quiet probe for a session the API made during a redirect. A 401 means
  // there is none.
  SESSION_USER: {
    method: 'GET',
    path: '/api/user/auth',
    requiresAuth: true,
  },
  // apiClient's base is /api: this is /api/user/profile-information, the
  // route the website and the apps both sign (not Fortify's cookie-only one).
  UPDATE_PROFILE: {
    method: 'PUT',
    path: '/user/profile-information',
    requiresAuth: true,
  },
  // Likewise /api/user/password, not Fortify's cookie-only /user/password.
  UPDATE_PROFILE_PASSWORD: {
    method: 'PUT',
    path: '/user/password',
    requiresAuth: true,
  },
  DELETE_ACCOUNT: {
    method: 'DELETE',
    path: '/api/account/delete',
    requiresAuth: true,
  },
  // Account data export — "download all of my data" (GET account/data/export).
  // Routed via `apiClient`, whose baseURL already includes `/api`, so the path
  // omits the prefix (unlike DELETE_ACCOUNT above, which uses the auth client).
  EXPORT_ACCOUNT_DATA: {
    method: 'GET',
    path: '/account/data/export',
    requiresAuth: true,
  },

  // Account security — routed via `apiClient`, so no `/api` prefix. Connecting,
  // disconnecting, a first password and asking to change the email answer 423
  // CONFIRMATION_REQUIRED until the session or bearer has confirmed it's the
  // owner in the last ten minutes, as the two-factor setup routes do.
  GET_IDENTITY_CONFIRMATION: {
    method: 'GET',
    path: '/account/confirmation',
    requiresAuth: true,
  },
  CONFIRM_IDENTITY: {
    method: 'POST',
    path: '/account/confirmation',
    requiresAuth: true,
  },
  LINK_GOOGLE: {
    method: 'POST',
    path: '/account/sign-in-methods/google',
    requiresAuth: true,
  },
  LINK_APPLE: {
    method: 'POST',
    path: '/account/sign-in-methods/apple',
    requiresAuth: true,
  },
  UNLINK_PROVIDER: {
    method: 'DELETE',
    path: '/account/sign-in-methods/{provider}',
    requiresAuth: true,
  },
  ADD_PASSWORD: {
    method: 'POST',
    path: '/account/password',
    requiresAuth: true,
  },
  SIGN_OUT_OTHER_DEVICES: {
    method: 'DELETE',
    path: '/account/sessions',
    requiresAuth: true,
  },
  REQUEST_EMAIL_CHANGE: {
    method: 'POST',
    path: '/account/email',
    requiresAuth: true,
  },
  CANCEL_EMAIL_CHANGE: {
    method: 'DELETE',
    path: '/account/email',
    requiresAuth: true,
  },
  // Answered from the emailed links' page with the link's token, which is the
  // credential: no session needed.
  APPROVE_EMAIL_CHANGE: {
    method: 'POST',
    path: '/account/email/approve',
    requiresAuth: false,
  },
  CONFIRM_EMAIL_CHANGE: {
    method: 'POST',
    path: '/account/email/confirm',
    requiresAuth: false,
  },

  // Two-factor setup: Fortify's routes at the API root, never the `/api`
  // base. The website's session only, with CSRF. Each answers 423
  // CONFIRMATION_REQUIRED without a confirmation from the last ten minutes.
  TWO_FACTOR_ENABLE: {
    method: 'POST',
    path: '/user/two-factor-authentication',
    requiresAuth: true,
  },
  TWO_FACTOR_DISABLE: {
    method: 'DELETE',
    path: '/user/two-factor-authentication',
    requiresAuth: true,
  },
  TWO_FACTOR_QR_CODE: {
    method: 'GET',
    path: '/user/two-factor-qr-code',
    requiresAuth: true,
  },
  TWO_FACTOR_SECRET_KEY: {
    method: 'GET',
    path: '/user/two-factor-secret-key',
    requiresAuth: true,
  },
  TWO_FACTOR_CONFIRM: {
    method: 'POST',
    path: '/user/confirmed-two-factor-authentication',
    requiresAuth: true,
  },
  TWO_FACTOR_RECOVERY_CODES: {
    method: 'GET',
    path: '/user/two-factor-recovery-codes',
    requiresAuth: true,
  },
  TWO_FACTOR_REGENERATE_RECOVERY_CODES: {
    method: 'POST',
    path: '/user/two-factor-recovery-codes',
    requiresAuth: true,
  },

  // Subscription & billing — REST resource on /subscription. The upgrade
  // preview is GET /subscription?preview=1&plan=… (same path, query flag).
  GET_SUBSCRIPTION: {
    method: 'GET',
    path: '/subscription',
    requiresAuth: true,
  },
  CREATE_SUBSCRIPTION: {
    method: 'POST',
    path: '/subscription',
    requiresAuth: true,
  },
  UPDATE_SUBSCRIPTION: {
    method: 'PUT',
    path: '/subscription',
    requiresAuth: true,
  },
  CANCEL_SUBSCRIPTION: {
    method: 'DELETE',
    path: '/subscription',
    requiresAuth: true,
  },
  START_TRIAL: {
    method: 'POST',
    path: '/trial',
    requiresAuth: true,
  },
  CANCEL_TRIAL: {
    method: 'DELETE',
    path: '/trial',
    requiresAuth: true,
  },
  // A GET with the required `return_url` query param.
  BILLING_PORTAL: {
    method: 'GET',
    path: '/billing/portal',
    requiresAuth: true,
  },

  // Uploads — one file per request; the answer's `data.path` is the stored
  // FILENAME, which is what a measurement's `media[]` takes. An upload that
  // isn't attached yet can be discarded; once attached the API answers 409.
  FILE_UPLOAD: {
    method: 'POST',
    path: '/file/upload',
    requiresAuth: true,
  },
  FILE_DISCARD: {
    method: 'DELETE',
    path: '/file/{name}',
    requiresAuth: true,
  },

  // Progress photos — remove one saved photo from a measurement entry. Owner
  // only, any plan, no two-factor; it deletes the file too and can't be undone.
  MEASUREMENT_IMAGE_DELETE: {
    method: 'DELETE',
    path: '/user-measurement/{measurement}/images/{image}',
    requiresAuth: true,
  },
} as const;

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Extract data type from SuccessApiResponse
 */
export type SuccessResponseData<T> =
  T extends SuccessApiResponse<infer U> ? U : never;

// PaginatedResponseData is re-exported from common/pagination.types above
// (single source of truth).

/**
 * API endpoint method type
 */
export type ApiMethod = ApiEndpoint['method'];

/**
 * API endpoint path type
 */
export type ApiPath = ApiEndpoint['path'];

/**
 * Generic service response type for GET requests
 * Automatically uses SuccessApiResponse wrapper
 */
export type GetApiResponse<T> = SuccessApiResponse<T>;

/**
 * Generic service response type for POST/PUT requests
 * Automatically uses CreatedApiResponse wrapper
 */
export type PostApiResponse = CreatedApiResponse;

/**
 * Generic service response type for POST/PUT requests with additional data
 * Extends CreatedApiResponse with custom data
 */
export type PostApiResponseWithData<T> = CreatedApiResponse & T;
